import { Check, Copy, Mail } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { type CreateInviteResult, useCreateInvite } from "@/hooks/use-members";

interface InviteModalProps {
  solutionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteModal(props: InviteModalProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-lg">{props.open && <ModalBody {...props} />}</DialogContent>
    </Dialog>
  );
}

function ModalBody({ solutionId, onOpenChange }: InviteModalProps) {
  const create = useCreateInvite(solutionId);
  const [email, setEmail] = useState("");
  const [created, setCreated] = useState<CreateInviteResult | null>(null);
  const [copied, setCopied] = useState(false);

  const inviteUrl = created ? `${window.location.origin}/accept?token=${created.token}` : "";

  async function submit() {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    try {
      const result = await create.mutateAsync({ email: trimmed });
      setCreated(result);
    } catch {
      // toasted by the mutation
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Failed to copy");
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display">Invite a member</DialogTitle>
        <DialogDescription>
          {created
            ? "Share this link with the invitee. They'll be asked to sign in or sign up before joining."
            : "Enter an email. We'll generate a single-use link valid for 7 days. There's no email sent — you copy the link and share it however you like."}
        </DialogDescription>
      </DialogHeader>

      {!created ? (
        <div className="space-y-3">
          <div>
            <label
              htmlFor="invite-email"
              className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5 block"
            >
              Email
            </label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@example.com"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") submit();
              }}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Role: <span className="font-medium text-foreground">member</span> (only role in v1).
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-md bg-muted/40 border border-[var(--color-border)] px-3 py-2 text-xs flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="font-medium">{created.email}</span>
            <span className="text-muted-foreground">·</span>
            <span className="text-muted-foreground">
              expires {new Date(created.expiresAt).toLocaleDateString()}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={inviteUrl}
              readOnly
              className="text-xs font-mono"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button variant="outline" size="sm" onClick={copyLink} className="shrink-0">
              {copied ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-3.5 w-3.5" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      <DialogFooter>
        {!created ? (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!email.trim() || create.isPending}>
              {create.isPending ? "Creating…" : "Create invite"}
            </Button>
          </>
        ) : (
          <Button onClick={() => onOpenChange(false)}>Done</Button>
        )}
      </DialogFooter>
    </>
  );
}
